import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import LoadingFormNormal from "components/shared-components/LoadingFormNormal";
import { notificationLink } from "../../../pages/NotificationManagement/utils";
import {
  usePopupNotificationList,
  useReadAllNotification,
  useReadNotification,
  useUploadTokenDevice,
} from "hooks";
import {
  getToken,
  getWebPushDeviceVersion,
  onMessageListener,
  registerFcmServiceWorker,
  requestPermission,
  FCM_HANDLED_PREFIX,
} from "firebaseInit";

const FCM_DEVICE_TOKEN_KEY = "FCM_DEVICE_TOKEN";

const TICKET_BROWSER_NOTIFICATION_TYPES = [
  "TICKET_CREATED",
  "TICKET_SEND_TO_SALE",
  "TICKET_PROCESSED",
  "TICKET_SKIPPED",
  "TICKET_COMPLETED",
];

const getFcmDisplayContent = payload => {
  const data = payload?.data || {};
  const notificationBlock = payload?.notification || {};

  return {
    title: notificationBlock.title || data.title || "",
    body: notificationBlock.body || data.body || "",
  };
};

const getFcmNavigationPath = payload => {
  const data = payload?.data || {};
  const notificationType = String(data.notification_type || "")
    .trim()
    .toUpperCase();

  if (notificationType === "TICKET_COMPLETED" && data.order_id) {
    return `/manage-orders/${data.order_id}`;
  }

  if (data.ticket_id) {
    const ticketBasePath = window.location.pathname.includes("/my-tickets")
      ? "/my-tickets"
      : "/ticket-management";

    return `${ticketBasePath}/${data.ticket_id}`;
  }

  if (data.order_id) {
    return `/manage-orders/${data.order_id}`;
  }

  return "/notification-management/list";
};

const NotificationItem = ({ notification, onClick, ...props }) => {
  let dtMakerDate = "";
  const linkNotification = notificationLink(notification);

  if (notification.maker_date) {
    dtMakerDate = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(Number(notification.maker_date) * 1000);
  }

  const onClickLink = () => {
    onClick && onClick();
  };

  return (
    <Link
      key={notification.id}
      to={linkNotification}
      className="text-reset notification-item"
      onClick={onClickLink}
    >
      <div className="media">
        <div className="avatar-xs me-3">
          <span className="avatar-title bg-primary rounded-circle font-size-16">
            <i className="fas fa-bell"></i>
          </span>
        </div>
        <div className="media-body">
          <h6 className="mt-0 mb-1">{notification.notification.title}</h6>
          <div>{notification.notification.description}</div>
          <div className="font-size-12 text-muted">
            {dtMakerDate && (
              <p className="mb-0">
                <i className="mdi mdi-clock-outline" /> {dtMakerDate}
              </p>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

const NotificationDropdown = props => {
  const { t } = useTranslation(["notification"]);
  const { addToast } = useToasts();
  const navigation = useNavigate();

  const [popupNotificationList, setPopupNotificationList] = useState([]);
  const [totalCountPopupNotification, setTotalCountPopupNotification] =
    useState(0);
  const [menu, setMenu] = useState(false);

  const onFetchPopupNotificationListSuccess = response => {
    setPopupNotificationList(response.items);
    setTotalCountPopupNotification(response.totalCount);
  };

  const {
    refetch: fetchPopupNotificationList,
    isFetching: isLoadingPopupNotificationList,
  } = usePopupNotificationList(onFetchPopupNotificationListSuccess);

  const onReadNotificationSuccess = () => {
    fetchPopupNotificationList();
  };

  const { mutate: onReadNotification } = useReadNotification(
    onReadNotificationSuccess
  );

  const onReadAllNotificationSuccess = () => {
    fetchPopupNotificationList();
  };

  const { refetch: onReadAllNotification } = useReadAllNotification(
    onReadAllNotificationSuccess
  );

  const { mutate: uploadTokenDevice } = useUploadTokenDevice();

  const isTicketBrowserNotification = payload => {
    const type = String(payload?.data?.notification_type || "")
      .trim()
      .toUpperCase();

    return TICKET_BROWSER_NOTIFICATION_TYPES.includes(type);
  };

  const renderNotification = payload => {
    const { title, body } = getFcmDisplayContent(payload);

    return (
      <div
        className="css-fire-base-notification-container"
        onClick={() => {
          onReadNotification(payload.data.notification_id);
          navigation(getFcmNavigationPath(payload));
        }}
      >
        <div className="css-fire-base-notification-title">{title}</div>
        <div>{body}</div>
      </div>
    );
  };

  const showBrowserNotification = payload => {
    const { title, body } = getFcmDisplayContent(payload);

    if (typeof window === "undefined" || !("Notification" in window)) {
      addToast(renderNotification(payload), {
        appearance: "success",
        autoDismiss: true,
      });
      return;
    }

    if (window.Notification.permission !== "granted") {
      addToast(renderNotification(payload), {
        appearance: "success",
        autoDismiss: true,
      });
      return;
    }

    const browserNotification = new window.Notification(title || "Thông báo", {
      body,
      icon: "/logo_login.png",
    });

    browserNotification.onclick = () => {
      onReadNotification(payload?.data?.notification_id);
      navigation(getFcmNavigationPath(payload));
      window.focus();
      browserNotification.close();
    };
  };

  useEffect(() => {
    const initFcmPush = async () => {
      await registerFcmServiceWorker();
      await requestPermission(tokenFunc);
    };

    initFcmPush();
    fetchPopupNotificationList();
  }, []);

  useEffect(() => {
    const handleStorageSync = event => {
      if (event.key?.startsWith(FCM_HANDLED_PREFIX)) {
        fetchPopupNotificationList();
      }
    };

    window.addEventListener("storage", handleStorageSync);

    return () => {
      window.removeEventListener("storage", handleStorageSync);
    };
  }, [fetchPopupNotificationList]);

  useEffect(() => {
    onMessageListener(payload => {
      fetchPopupNotificationList();

      if (isTicketBrowserNotification(payload)) {
        showBrowserNotification(payload);
        return;
      }

      addToast(renderNotification(payload), {
        appearance: "success",
        autoDismiss: true,
      });
    });
  }, []);

  const tokenFunc = async () => {
    const token = await getToken();
    if (token) {
      localStorage.setItem(FCM_DEVICE_TOKEN_KEY, token);
      uploadTokenDevice({
        device_token: token,
        os_type: "web",
        device_version: getWebPushDeviceVersion(),
      });
    }
    return token;
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          {totalCountPopupNotification && totalCountPopupNotification > 0 ? (
            <React.Fragment>
              <i className="bx bx-bell bx-tada" />
              <span className="badge bg-danger rounded-pill">
                {totalCountPopupNotification}
              </span>
            </React.Fragment>
          ) : (
            <i className="bx bx-bell" />
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu dropdown-menu-lg p-0 dropdown-menu-end">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0">{t("notification")}</h6>
              </Col>
              <div className="col-auto">
                {totalCountPopupNotification != 0 && (
                  <Link
                    onClick={() => onReadAllNotification()}
                    to="#"
                    className="small"
                  >
                    {t("read_all")}
                  </Link>
                )}
              </div>
            </Row>
          </div>

          <SimpleBar style={{ height: "330px" }}>
            {isLoadingPopupNotificationList ? (
              <LoadingFormNormal
                countInput="4"
                countButton="0"
                formClass="row-cols-1"
                buttonClass="justify-content-end"
                buttonColClass="col-3"
              />
            ) : (
              <React.Fragment>
                {popupNotificationList && popupNotificationList.length > 0 ? (
                  <React.Fragment>
                    {popupNotificationList.map(item => (
                      <NotificationItem
                        key={item.id}
                        notification={item}
                        onClick={() => {
                          onReadNotification(item.notification_id);
                          setMenu(false);
                        }}
                      />
                    ))}
                  </React.Fragment>
                ) : (
                  <div className="card">
                    <div className="card-body text-center">
                      {t("no_new_message")}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}
          </SimpleBar>
          <div className="p-2 border-top d-grid">
            <Link
              className="btn btn-sm btn-link font-size-14 btn-block text-center"
              to="/notification-management/list"
            >
              <i className="mdi mdi-arrow-right-circle me-1"></i>
              {t("view_all")}
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
